import { useEffect, useRef, useState } from "react";
import {
    Phone,
    MessageCircle,
    MapPin,
    Clock,
    ArrowUpRight,
} from "lucide-react";
import { CONTACT, WHATSAPP_URL, TEL_URL } from "../data/site";

function LazyMap() {
    const wrapRef = useRef(null);
    const [load, setLoad] = useState(false);

    useEffect(() => {
        if (!wrapRef.current) return;
        const io = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    setLoad(true);
                    io.disconnect();
                }
            },
            { rootMargin: "200px" }
        );
        io.observe(wrapRef.current);
        return () => io.disconnect();
    }, []);

    return (
        <div
            ref={wrapRef}
            className="relative w-full aspect-[4/3] lg:aspect-square rounded-2xl overflow-hidden border border-[#c9a24b]/25 bg-[#14141a]"
            data-testid="contact-map"
        >
            {load ? (
                <iframe
                    title="Cegu Masaj Salonu Konumu"
                    src="https://www.google.com/maps?q=Cegu+Masaj+Salonu+Ozluce+Bursa&output=embed"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="w-full h-full"
                    style={{ filter: "grayscale(0.65) contrast(1.05)" }}
                />
            ) : (
                <div className="w-full h-full flex items-center justify-center text-[#7d766a] text-sm">
                    Harita yükleniyor…
                </div>
            )}
        </div>
    );
}

export default function Contact() {
    const items = [
        {
            icon: Phone,
            label: "Telefon",
            value: CONTACT.phone,
            href: TEL_URL,
            test: "contact-phone",
        },
        {
            icon: MessageCircle,
            label: "WhatsApp",
            value: CONTACT.whatsapp,
            href: WHATSAPP_URL,
            test: "contact-whatsapp",
            external: true,
        },
        {
            icon: MapPin,
            label: "Adres",
            value: CONTACT.fullAddress,
            href: CONTACT.mapLink,
            test: "contact-address",
            external: true,
        },
        {
            icon: Clock,
            label: "Çalışma Saatleri",
            value: CONTACT.hours,
            test: "contact-hours",
        },
    ];

    return (
        <section
            id="iletisim"
            data-testid="contact-section"
            className="section bg-[#0f0f11] border-t border-[#23232b]"
        >
            <div className="container-x grid lg:grid-cols-12 gap-10 lg:gap-16">
                <div className="lg:col-span-5">
                    <div className="eyebrow mb-4">İletişim</div>
                    <h2 className="h2 text-[#f4efe4]">
                        Bize <span className="gold-text">Ulaşın</span>
                    </h2>
                    <div className="divider-gold mt-6" />
                    <p className="mt-7 body-base max-w-md">
                        Bursa Nilüfer Özlüce'de, Batı Palas içerisindeyiz.
                        Randevunuzu WhatsApp üzerinden dakikalar içinde
                        oluşturabilirsiniz.
                    </p>

                    <ul className="mt-8 divide-y divide-[#23232b] border-y border-[#23232b]">
                        {items.map((it) => {
                            const Icon = it.icon;
                            const Cmp = it.href ? "a" : "div";
                            return (
                                <li key={it.label}>
                                    <Cmp
                                        {...(it.href
                                            ? {
                                                  href: it.href,
                                                  target: it.external
                                                      ? "_blank"
                                                      : undefined,
                                                  rel: it.external
                                                      ? "noreferrer"
                                                      : undefined,
                                              }
                                            : {})}
                                        data-testid={it.test}
                                        className="group flex items-center gap-4 py-4"
                                    >
                                        <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#c9a24b] to-[#8a6a28] flex items-center justify-center shrink-0 shadow-md shadow-[#c9a24b]/25">
                                            <Icon className="w-4 h-4 text-[#0a0a0c]" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[10.5px] uppercase tracking-[0.24em] text-[#7d766a]">
                                                {it.label}
                                            </div>
                                            <div className="text-[15px] text-[#f4efe4] group-hover:text-[#f5d685] transition mt-0.5">
                                                {it.value}
                                            </div>
                                        </div>
                                        {it.href && (
                                            <ArrowUpRight className="w-4 h-4 text-[#7d766a] group-hover:text-[#f5d685] transition" />
                                        )}
                                    </Cmp>
                                </li>
                            );
                        })}
                    </ul>

                    <a
                        href={CONTACT.mapLink}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="contact-directions"
                        className="btn btn-gold mt-8"
                    >
                        <MapPin className="w-4 h-4" />
                        Yol Tarifi Al
                    </a>
                </div>

                <div className="lg:col-span-7">
                    <LazyMap />
                </div>
            </div>
        </section>
    );
}
